import { useCallback, useSyncExternalStore } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import {
  getPinnedChatLayout,
  isChatLayoutView,
  requestCollapseAppSidebar,
  setPinnedChatLayout,
  subscribePinnedChatLayout,
  type ChatLayoutView,
  type PinnedChatLayoutView,
} from "@/lib/chat/chat-layout-persistence";
import { isMobileViewport } from "@/hooks/use-mobile";

function getPinnedSnapshot(): PinnedChatLayoutView | null {
  return getPinnedChatLayout();
}

function getPinnedServerSnapshot(): null {
  return null;
}

function shouldCollapseSidebarForView(
  value: string,
  collapseSidebar: boolean | undefined,
): boolean {
  return (
    collapseSidebar !== false &&
    (value === "split-view" || value === "chat")
  );
}

function resolveSelectedView(
  urlView: string | null,
  pinnedLayout: PinnedChatLayoutView | null,
): ChatLayoutView {
  let view: ChatLayoutView = "app";
  if (isChatLayoutView(urlView)) {
    view = urlView;
  } else if (pinnedLayout) {
    view = pinnedLayout;
  }
  // Sync check — avoid useIsMobile here so this hook’s call count stays stable
  // for every consumer (ChatView still coerces layout for render).
  if (view === "split-view" && isMobileViewport()) {
    return "chat";
  }
  return view;
}

export const useChatSwitch = () => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const pinnedLayout = useSyncExternalStore(
    subscribePinnedChatLayout,
    getPinnedSnapshot,
    getPinnedServerSnapshot,
  );

  const urlView = searchParams.get("view");
  const selectedView = resolveSelectedView(urlView, pinnedLayout);

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      return params.toString();
    },
    [searchParams],
  );

  const handleViewChange = useCallback(
    (
      value: string,
      options?: {
        /** When false, split-view opens without collapsing the app sidebar. Default: true */
        collapseSidebar?: boolean;
      },
    ) => {
      if (!isChatLayoutView(value)) {
        return;
      }

      const nextView: ChatLayoutView =
        value === "split-view" && isMobileViewport() ? "chat" : value;

      const openingChat =
        nextView !== "app" &&
        shouldCollapseSidebarForView(nextView, options?.collapseSidebar);

      if (nextView === "app") {
        setPinnedChatLayout(null);
      } else {
        setPinnedChatLayout(nextView);
      }

      if (openingChat) {
        requestCollapseAppSidebar();
      }

      if (nextView !== selectedView) {
        const queryString = createQueryString("view", nextView);
        router.push(`${pathname}?${queryString}`);
      } else if (nextView !== "app" && !pinnedLayout) {
        setPinnedChatLayout(nextView);
      }
    },
    [pathname, createQueryString, router, selectedView, pinnedLayout],
  );

  return {
    selectedView,
    handleViewChange,
    isChatPinned: pinnedLayout !== null,
  };
};
